import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Book, Plus, Trash2, Edit3, Image, ChevronRight, ChevronLeft,
  Eye, Lock, X, Loader2, ImagePlus, Share2, Camera,
  ExternalLink, Copy, Check, Mail, LayoutGrid, BookOpen
} from 'lucide-react';
import axios from 'axios';
import { getApiUrl } from '../utils';
import { cacheSet, cacheGet } from '../utils/offlineCache';

const API_URL = getApiUrl();

const STYLES = [
  { id: 'editorial', name: 'Editorial', description: 'High fashion magazine style' },
  { id: 'casual', name: 'Casual', description: 'Relaxed lifestyle look' },
  { id: 'luxury', name: 'Luxury', description: 'Premium designer aesthetic' },
  { id: 'street', name: 'Street', description: 'Urban streetwear vibe' },
  { id: 'minimal', name: 'Minimal', description: 'Clean, simple presentation' }
];

const SEASONS = [
  { id: 'all-season', name: 'All Season' },
  { id: 'spring', name: 'Spring' },
  { id: 'summer', name: 'Summer' },
  { id: 'fall', name: 'Fall' },
  { id: 'winter', name: 'Winter' }
];

export default function Lookbooks() {
  const navigate = useNavigate();
  const [lookbooks, setLookbooks] = useState([]);
  const [selectedLookbook, setSelectedLookbook] = useState(null);
  const [lookbookItems, setLookbookItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load cached lookbooks immediately
  useEffect(() => {
    cacheGet('lookbooks').then(cached => {
      if (cached?.data?.length) { setLookbooks(cached.data); setLoading(false); }
    });
  }, []);
  const [error, setError] = useState(null);
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [editingLookbook, setEditingLookbook] = useState(null);
  
  // Form state
  const [newLookbook, setNewLookbook] = useState({
    name: '',
    description: '',
    style: 'casual',
    season: 'all-season',
    isPublic: false
  });
  
  // Add item tab
  const [addItemTab, setAddItemTab] = useState('wardrobe');

  // Share
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);

  // Magazine view
  const [viewMode, setViewMode] = useState('magazine'); // 'magazine' or 'grid'
  const [currentPage, setCurrentPage] = useState(0);

  // Wardrobe items for adding
  const [wardrobeItems, setWardrobeItems] = useState([]);
  const [tryonResults, setTryonResults] = useState([]);
  const [savedProducts, setSavedProducts] = useState([]);
  const [studioSaves, setStudioSaves] = useState([]);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Fetch all lookbooks
  const fetchLookbooks = useCallback(async () => {
    try {
      if (lookbooks.length === 0) setLoading(true);
      const response = await axios.get(`${API_URL}/lookbooks`, { headers });
      const data = response.data.lookbooks || [];
      setLookbooks(data);
      cacheSet('lookbooks', data);
    } catch (err) {
      // Fallback to cache on error
      const cached = await cacheGet('lookbooks');
      if (cached?.data) {
        setLookbooks(cached.data);
      } else {
        setError('Failed to load lookbooks');
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch lookbook details
  const fetchLookbookDetails = useCallback(async (id) => {
    try {
      const response = await axios.get(`${API_URL}/lookbooks/${id}`, { headers });
      setSelectedLookbook(response.data.lookbook);
      setLookbookItems(response.data.items || []);
      setCurrentPage(0);
    } catch (err) {
      setError('Failed to load lookbook details');
      console.error(err);
    }
  }, []);

  // Fetch items for adding
  const fetchItemsForAdding = useCallback(async () => {
    try {
      const [wardrobeRes, savedRes] = await Promise.all([
        axios.get(`${API_URL}/real-wardrobe`, { headers }),
        axios.get(`${API_URL}/saved`, { headers })
      ]);
      setWardrobeItems(wardrobeRes.data.items || []);
      setSavedProducts(savedRes.data.items || []);
      
      // Try to get try-on results
      try {
        const tryonRes = await axios.get(`${API_URL}/tryon/results`, { headers });
        setTryonResults(tryonRes.data.results || []);
      } catch {
        setTryonResults([]);
      }
      
      // Load studio saves from localStorage
      try {
        const styleStudio = JSON.parse(localStorage.getItem('styleStudioOutfits') || '[]');
        const aiStudio = JSON.parse(localStorage.getItem('aiStudioSaves') || '[]');
        const combined = [
          ...styleStudio.map(s => ({ ...s, source: 'style-studio' })),
          ...aiStudio.map(s => ({ ...s, source: 'ai-studio' }))
        ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setStudioSaves(combined);
      } catch {
        setStudioSaves([]);
      }
    } catch (err) {
      console.error('Failed to fetch items:', err);
    }
  }, []);

  useEffect(() => {
    fetchLookbooks();
  }, [fetchLookbooks]);

  // Create lookbook
  const handleCreateLookbook = async () => {
    if (!newLookbook.name.trim()) return;
    
    try {
      const response = await axios.post(`${API_URL}/lookbooks`, newLookbook, { headers });
      setLookbooks([response.data.lookbook, ...lookbooks]);
      setShowCreateModal(false);
      setNewLookbook({ name: '', description: '', style: 'casual', season: 'all-season', isPublic: false });
    } catch (err) {
      if (err.response?.status === 403) {
        setError(err.response.data?.message || 'Usage limit reached. Please upgrade your plan.');
      } else {
        setError('Failed to create lookbook');
      }
    }
  };

  // Update lookbook
  const handleUpdateLookbook = async () => {
    if (!editingLookbook) return;
    
    try {
      const response = await axios.put(
        `${API_URL}/lookbooks/${editingLookbook.id}`,
        editingLookbook,
        { headers }
      );
      setLookbooks(lookbooks.map(lb => 
        lb.id === editingLookbook.id ? response.data.lookbook : lb
      ));
      if (selectedLookbook?.id === editingLookbook.id) {
        setSelectedLookbook(response.data.lookbook);
      }
      setEditingLookbook(null);
    } catch (err) {
      setError('Failed to update lookbook');
    }
  };

  // Delete lookbook
  const handleDeleteLookbook = async (id) => {
    if (!window.confirm('Delete this lookbook?')) return;
    
    try {
      await axios.delete(`${API_URL}/lookbooks/${id}`, { headers });
      setLookbooks(lookbooks.filter(lb => lb.id !== id));
      if (selectedLookbook?.id === id) {
        setSelectedLookbook(null);
        setLookbookItems([]);
      }
    } catch (err) {
      setError('Failed to delete lookbook');
    }
  };

  // Add item to lookbook
  const handleAddItem = async (type, itemId, studioItem) => {
    if (!selectedLookbook) return;
    
    try {
      let endpoint = `${API_URL}/lookbooks/${selectedLookbook.id}/items`;
      let body = {};
      
      if (type === 'wardrobe') {
        endpoint += '/from-wardrobe';
        body = { wardrobeItemId: itemId };
      } else if (type === 'tryon') {
        endpoint += '/from-tryon';
        body = { tryonResultId: itemId };
      } else if (type === 'saved') {
        endpoint += '/from-saved';
        body = { productId: itemId };
      } else if (type === 'studio') {
        // Studio saves use customImageUrl
        const img = studioItem?.thumbnail || studioItem?.image_url || studioItem?.image;
        const title = studioItem?.name || studioItem?.title || (studioItem?.source === 'ai-studio' ? 'AI Studio Creation' : 'Style Studio Outfit');
        body = { customImageUrl: img, customTitle: title };
      }
      
      await axios.post(endpoint, body, { headers });
      fetchLookbookDetails(selectedLookbook.id);
      setShowAddItemModal(false);
    } catch (err) {
      setError('Failed to add item');
    }
  };

  // Remove item from lookbook
  const handleRemoveItem = async (itemId) => {
    if (!selectedLookbook) return;
    
    try {
      await axios.delete(`${API_URL}/lookbooks/${selectedLookbook.id}/items/${itemId}`, { headers });
      setLookbookItems(lookbookItems.filter(item => item.id !== itemId));
    } catch (err) {
      setError('Failed to remove item');
    }
  };

  // Get style emoji
  const getStyleEmoji = (style) => {
    const emojis = { editorial: '📸', casual: '☀️', luxury: '💎', street: '🔥', minimal: '🤍' };
    return emojis[style] || '✨';
  };

  // Get season emoji
  const getSeasonEmoji = (season) => {
    const emojis = { 'spring': '🌸', 'summer': '🌊', 'fall': '🍂', 'winter': '❄️', 'all-season': '🌈' };
    return emojis[season] || '🌈';
  };

  // Share collection
  const getShareText = () => {
    if (!selectedLookbook) return '';
    const items = lookbookItems.length;
    return `Check out my "${selectedLookbook.name}" collection on fashionDisc! ${items} curated ${selectedLookbook.style} pieces 🔥`;
  };

  const handleShare = (method) => {
    const text = getShareText();
    const url = window.location.href;
    
    if (method === 'copy') {
      navigator.clipboard.writeText(`${text}\n${url}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else if (method === 'whatsapp') {
      window.open(`https://wa.me/?text=${encodeURIComponent(text + '\n' + url)}`, '_blank');
    } else if (method === 'email') {
      window.open(`mailto:?subject=${encodeURIComponent(`My ${selectedLookbook.name} Collection`)}&body=${encodeURIComponent(text + '\n\n' + url)}`, '_blank');
    } else if (method === 'messages') {
      window.open(`sms:&body=${encodeURIComponent(text + ' ' + url)}`, '_blank');
    } else if (method === 'native') {
      if (navigator.share) {
        navigator.share({ title: selectedLookbook.name, text, url }).catch(() => {});
      }
    }
    setShowShareMenu(false);
  };

  // Get image URL for an item
  const getItemImage = (item) => {
    return item.enhanced_image_url || 
           item.custom_image_url || 
           item.wardrobe_image || 
           item.tryon_image || 
           item.product_image ||
           '/placeholder-fashion.jpg';
  };

  // Get item title
  const getItemTitle = (item) => {
    return item.custom_title ||
           item.wardrobe_name ||
           item.product_name ||
           (item.tryon_garment_type ? `Try-On: ${item.tryon_garment_type}` : 'Outfit');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
          <p className="text-sm text-stone-400">Loading your stylebook…</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -10 }} 
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-10 gap-4"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full border border-stone-300 flex items-center justify-center">
              <Book className="w-6 h-6 text-stone-600" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-stone-400 mb-0.5">CURATED</p>
              <h1 className="text-3xl font-extralight text-stone-800" style={{ fontFamily: 'Georgia, serif' }}>My Stylebook</h1>
              <p className="text-xs uppercase tracking-[0.1em] text-stone-400 mt-0.5">Collections from your wardrobe, try-ons & saved items</p>
            </div>
          </div>
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-5 py-2.5 bg-stone-800 hover:bg-stone-900 text-white rounded-full text-xs uppercase tracking-[0.15em] font-medium transition-all"
          >
            <Plus className="w-5 h-5" />
            New Collection
          </motion.button>
        </motion.div>

        <AnimatePresence>
          {error && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }} 
              animate={{ opacity: 1, y: 0 }} 
              exit={{ opacity: 0, y: -10 }}
              className="mb-6 p-4 bg-red-50/80 backdrop-blur-sm border border-red-200/60 rounded-2xl text-red-700 flex items-center justify-between"
            >
              <span>{error}</span>
              <button onClick={() => setError(null)} className="ml-2 p-1 hover:bg-red-100 rounded-lg transition-colors">
                <X className="w-4 h-4" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="max-w-3xl mx-auto space-y-3">
            <h2 className="text-xs font-medium text-stone-400 uppercase tracking-[0.15em] mb-4">Your Collections</h2>
            
            {lookbooks.length === 0 ? (
              <motion.div 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }}
                className="text-center py-14 bg-white/60 backdrop-blur-sm rounded-2xl border border-stone-200/40"
              >
                <div className="w-14 h-14 mx-auto rounded-2xl bg-stone-100 flex items-center justify-center mb-4">
                  <Book className="w-7 h-7 text-stone-300" />
                </div>
                <p className="text-stone-500 font-medium">No collections yet</p>
                <p className="text-sm text-stone-400 mt-1">Create one to start curating!</p>
              </motion.div>
            ) : (
              <div className="space-y-2">
                {lookbooks.map((lookbook, index) => (
                  <React.Fragment key={lookbook.id}>
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => selectedLookbook?.id === lookbook.id ? (setSelectedLookbook(null), setLookbookItems([])) : fetchLookbookDetails(lookbook.id)}
                    className={`p-4 rounded-2xl cursor-pointer transition-all duration-200 ${
                      selectedLookbook?.id === lookbook.id 
                        ? 'bg-white shadow-md shadow-stone-200/50 ring-1 ring-suede-300' 
                        : 'bg-white/50 backdrop-blur-sm hover:bg-white hover:shadow-sm'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-stone-800 truncate">{lookbook.name}</h3>
                          {lookbook.is_public ? (
                            <Eye className="w-3.5 h-3.5 text-stone-300 flex-shrink-0" />
                          ) : (
                            <Lock className="w-3.5 h-3.5 text-stone-300 flex-shrink-0" />
                          )}
                        </div>
                        {lookbook.description && (
                          <p className="text-xs text-stone-400 mt-0.5 line-clamp-1">{lookbook.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-stone-400 bg-stone-100 px-2 py-0.5 rounded-full">
                            {lookbook.item_count || 0} items
                          </span>
                          <span className="text-xs text-stone-400 capitalize">
                            {lookbook.season?.replace('-', ' ')}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className={`w-4 h-4 text-stone-300 flex-shrink-0 transition-transform ${selectedLookbook?.id === lookbook.id ? 'rotate-90 text-suede-400' : ''}`} />
                    </div>
                  </motion.div>
                  <AnimatePresence>
                  {selectedLookbook?.id === lookbook.id && (
                    <motion.div 
                      key={`detail-${selectedLookbook.id}`}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -5 }}
                      className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-sm border border-stone-200/40 p-5 mt-1"
                    >
                {/* Detail Header */}
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h2 className="text-2xl font-light text-stone-800" style={{ fontFamily: 'Georgia, serif' }}>{selectedLookbook.name}</h2>
                    </div>
                    {selectedLookbook.description && (
                      <p className="text-stone-400 text-sm mt-1">{selectedLookbook.description}</p>
                    )}
                    <div className="flex items-center gap-2 mt-3">
                      <span className="px-3 py-1 bg-suede-50 text-suede-600 rounded-full text-xs font-medium capitalize">
                        {selectedLookbook.style}
                      </span>
                      <span className="px-3 py-1 bg-stone-100 text-stone-500 rounded-full text-xs font-medium capitalize">
                        {selectedLookbook.season?.replace('-', ' ')}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => { fetchItemsForAdding(); setShowAddItemModal(true); }}
                      className="p-2.5 text-stone-400 hover:text-suede-500 hover:bg-suede-50 rounded-xl transition-colors"
                      title="Add Item"
                    >
                      <ImagePlus className="w-5 h-5" />
                    </motion.button>
                    <div className="relative">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => setShowShareMenu(!showShareMenu)}
                        className="p-2.5 text-stone-400 hover:text-suede-500 hover:bg-suede-50 rounded-xl transition-colors"
                        title="Share"
                      >
                        <Share2 className="w-5 h-5" />
                      </motion.button>
                      <AnimatePresence>
                        {showShareMenu && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: -5 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: -5 }}
                            className="absolute right-0 top-12 w-52 bg-white rounded-2xl shadow-xl border border-stone-200/60 py-2 z-50"
                          >
                            <button onClick={() => handleShare('whatsapp')} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-stone-600 hover:bg-stone-50 transition-colors">
                              <span className="text-lg">💬</span> WhatsApp
                            </button>
                            <button onClick={() => handleShare('messages')} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-stone-600 hover:bg-stone-50 transition-colors">
                              <span className="text-lg">💭</span> Messages
                            </button>
                            <button onClick={() => handleShare('email')} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-stone-600 hover:bg-stone-50 transition-colors">
                              <Mail className="w-4 h-4 text-stone-400" /> Email
                            </button>
                            <div className="border-t border-stone-100 my-1" />
                            <button onClick={() => handleShare('copy')} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-stone-600 hover:bg-stone-50 transition-colors">
                              {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-stone-400" />}
                              {copied ? 'Copied!' : 'Copy link'}
                            </button>
                            {navigator.share && (
                              <button onClick={() => handleShare('native')} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-stone-600 hover:bg-stone-50 transition-colors">
                                <ExternalLink className="w-4 h-4 text-stone-400" /> More options…
                              </button>
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setEditingLookbook(selectedLookbook)}
                      className="p-2.5 text-stone-400 hover:text-suede-500 hover:bg-suede-50 rounded-xl transition-colors"
                      title="Edit"
                    >
                      <Edit3 className="w-5 h-5" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleDeleteLookbook(selectedLookbook.id)}
                      className="p-2.5 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-5 h-5" />
                    </motion.button>
                  </div>
                </div>

                {/* View Mode Toggle */}
                <div className="flex items-center justify-between mb-4">
                  <p className="text-xs text-stone-400 uppercase tracking-[0.15em]">{lookbookItems.length} piece{lookbookItems.length !== 1 ? 's' : ''}</p>
                  <div className="flex items-center gap-1 bg-stone-100 rounded-xl p-1">
                    <button
                      onClick={() => setViewMode('magazine')}
                      className={`p-1.5 rounded-lg transition-all ${viewMode === 'magazine' ? 'bg-white shadow-sm text-stone-800' : 'text-stone-400 hover:text-stone-600'}`}
                      title="Magazine View"
                    >
                      <BookOpen className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-1.5 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-white shadow-sm text-stone-800' : 'text-stone-400 hover:text-stone-600'}`}
                      title="Grid View"
                    >
                      <LayoutGrid className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Items Display */}
                {lookbookItems.length === 0 ? (
                  <div className="text-center py-20 border-2 border-dashed border-stone-200/60 rounded-2xl">
                    <div className="w-16 h-16 mx-auto rounded-2xl bg-stone-100 flex items-center justify-center mb-4">
                      <Image className="w-8 h-8 text-stone-300" />
                    </div>
                    <p className="text-stone-500 font-medium mb-1">No items yet</p>
                    <p className="text-sm text-stone-400 mb-4">Add pieces from your wardrobe, try-ons or saved items</p>
                    <motion.button
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => { fetchItemsForAdding(); setShowAddItemModal(true); }}
                      className="px-5 py-2.5 bg-stone-800 hover:bg-stone-900 text-white rounded-full text-xs uppercase tracking-[0.15em] font-medium transition-all"
                    >
                      Add Your First Item
                    </motion.button>
                  </div>
                ) : viewMode === 'grid' ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {lookbookItems.map((item, index) => (
                      <motion.div 
                        key={item.id} 
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.05 }}
                        className="group relative aspect-[3/4] rounded-2xl overflow-hidden bg-stone-100"
                      >
                        <img
                          src={getItemImage(item)}
                          alt={getItemTitle(item)}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity duration-200">
                          <div className="absolute bottom-0 left-0 right-0 p-3">
                            <p className="text-white font-medium text-sm truncate">{getItemTitle(item)}</p>
                            {item.tags && item.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {item.tags.slice(0, 2).map((tag, i) => (
                                  <span key={i} className="text-xs text-white/80 bg-white/20 px-2 py-0.5 rounded-full backdrop-blur-sm">
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="absolute top-2 right-2">
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={(e) => { e.stopPropagation(); handleRemoveItem(item.id); }}
                              className="p-2 bg-white/90 backdrop-blur-sm rounded-xl hover:bg-white transition-colors shadow-sm"
                              title="Remove"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </motion.button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  /* Magazine / Book Layout — Mobile-Friendly Single Page with Swipe */
                  <div className="relative"
                    onTouchStart={(e) => {
                      const touch = e.touches[0];
                      e.currentTarget._touchStartX = touch.clientX;
                      e.currentTarget._touchStartY = touch.clientY;
                    }}
                    onTouchEnd={(e) => {
                      if (!e.currentTarget._touchStartX) return;
                      const touch = e.changedTouches[0];
                      const diffX = touch.clientX - e.currentTarget._touchStartX;
                      const diffY = touch.clientY - e.currentTarget._touchStartY;
                      // Only swipe if horizontal movement > 50px and more horizontal than vertical
                      if (Math.abs(diffX) > 50 && Math.abs(diffX) > Math.abs(diffY)) {
                        if (diffX < 0 && currentPage < lookbookItems.length - 1) {
                          setCurrentPage(currentPage + 1);
                        } else if (diffX > 0 && currentPage > 0) {
                          setCurrentPage(currentPage - 1);
                        }
                      }
                      e.currentTarget._touchStartX = null;
                      e.currentTarget._touchStartY = null;
                    }}
                  >
                    {/* Single item per page on mobile, two-page spread on desktop */}
                    <div className="relative bg-gradient-to-br from-stone-50 to-stone-100 rounded-2xl border border-stone-200/60 overflow-hidden shadow-inner"
                      style={{ minHeight: '420px' }}
                    >
                      {/* Desktop: spine shadow */}
                      <div className="absolute left-1/2 top-0 bottom-0 w-px bg-stone-300/40 hidden md:block" />
                      <div className="absolute left-1/2 top-0 bottom-0 w-4 -ml-2 bg-gradient-to-r from-transparent via-stone-300/20 to-transparent hidden md:block" />

                      <AnimatePresence mode="wait">
                        <motion.div
                          key={currentPage}
                          initial={{ opacity: 0, x: 30 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -30 }}
                          transition={{ duration: 0.25, ease: 'easeInOut' }}
                          className="h-full"
                          style={{ minHeight: '420px' }}
                        >
                          {/* Mobile: single item per page */}
                          <div className="md:hidden flex flex-col h-full" style={{ minHeight: '420px' }}>
                            {lookbookItems[currentPage] && (
                              <div className="relative flex flex-col flex-1">
                                <div className="flex-1 relative p-4 flex items-center justify-center bg-white/60">
                                  <img
                                    src={getItemImage(lookbookItems[currentPage])}
                                    alt={getItemTitle(lookbookItems[currentPage])}
                                    className="max-w-full max-h-[340px] object-contain rounded-lg shadow-md"
                                  />
                                  <div className="absolute top-3 right-3">
                                    <motion.button
                                      whileHover={{ scale: 1.1 }}
                                      whileTap={{ scale: 0.9 }}
                                      onClick={() => handleRemoveItem(lookbookItems[currentPage].id)}
                                      className="p-2 bg-white/90 backdrop-blur-sm rounded-xl hover:bg-white transition-colors shadow-sm"
                                    >
                                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                    </motion.button>
                                  </div>
                                </div>
                                <div className="px-5 py-3 bg-white/80">
                                  <p className="font-medium text-stone-800 text-sm" style={{ fontFamily: 'Georgia, serif' }}>
                                    {getItemTitle(lookbookItems[currentPage])}
                                  </p>
                                  {lookbookItems[currentPage].tags && lookbookItems[currentPage].tags.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-1.5">
                                      {lookbookItems[currentPage].tags.slice(0, 3).map((tag, i) => (
                                        <span key={i} className="text-[10px] text-stone-500 bg-stone-100 px-2 py-0.5 rounded-full">
                                          {tag}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  <p className="text-[10px] text-stone-400 mt-1.5 italic">Page {currentPage + 1} of {lookbookItems.length}</p>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Desktop: two-page spread (unchanged behavior) */}
                          <div className="hidden md:grid md:grid-cols-2 h-full" style={{ minHeight: '420px' }}>
                            {/* Left page */}
                            {lookbookItems[Math.floor(currentPage / 2) * 2] && (
                              <div className="relative flex flex-col border-r border-stone-200/40">
                                <div className="flex-1 relative p-4 flex items-center justify-center bg-white/60">
                                  <img
                                    src={getItemImage(lookbookItems[Math.floor(currentPage / 2) * 2])}
                                    alt={getItemTitle(lookbookItems[Math.floor(currentPage / 2) * 2])}
                                    className="max-w-full max-h-[340px] object-contain rounded-lg shadow-md"
                                  />
                                  <div className="absolute top-3 right-3">
                                    <motion.button
                                      whileHover={{ scale: 1.1 }}
                                      whileTap={{ scale: 0.9 }}
                                      onClick={() => handleRemoveItem(lookbookItems[Math.floor(currentPage / 2) * 2].id)}
                                      className="p-1.5 bg-white/90 backdrop-blur-sm rounded-lg hover:bg-white transition-colors shadow-sm opacity-0 hover:opacity-100"
                                    >
                                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                    </motion.button>
                                  </div>
                                </div>
                                <div className="px-5 py-3 bg-white/80">
                                  <p className="font-medium text-stone-800 text-sm" style={{ fontFamily: 'Georgia, serif' }}>
                                    {getItemTitle(lookbookItems[Math.floor(currentPage / 2) * 2])}
                                  </p>
                                </div>
                              </div>
                            )}
                            {/* Right page */}
                            {lookbookItems[Math.floor(currentPage / 2) * 2 + 1] ? (
                              <div className="relative flex flex-col">
                                <div className="flex-1 relative p-4 flex items-center justify-center bg-white/60">
                                  <img
                                    src={getItemImage(lookbookItems[Math.floor(currentPage / 2) * 2 + 1])}
                                    alt={getItemTitle(lookbookItems[Math.floor(currentPage / 2) * 2 + 1])}
                                    className="max-w-full max-h-[340px] object-contain rounded-lg shadow-md"
                                  />
                                  <div className="absolute top-3 right-3">
                                    <motion.button
                                      whileHover={{ scale: 1.1 }}
                                      whileTap={{ scale: 0.9 }}
                                      onClick={() => handleRemoveItem(lookbookItems[Math.floor(currentPage / 2) * 2 + 1].id)}
                                      className="p-1.5 bg-white/90 backdrop-blur-sm rounded-lg hover:bg-white transition-colors shadow-sm opacity-0 hover:opacity-100"
                                    >
                                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                    </motion.button>
                                  </div>
                                </div>
                                <div className="px-5 py-3 bg-white/80">
                                  <p className="font-medium text-stone-800 text-sm" style={{ fontFamily: 'Georgia, serif' }}>
                                    {getItemTitle(lookbookItems[Math.floor(currentPage / 2) * 2 + 1])}
                                  </p>
                                </div>
                              </div>
                            ) : (
                              <div className="relative flex flex-col items-center justify-center bg-white/30">
                                <motion.button
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  onClick={() => { fetchItemsForAdding(); setShowAddItemModal(true); }}
                                  className="flex flex-col items-center gap-3 p-6 text-stone-400 hover:text-stone-600 transition-colors"
                                >
                                  <div className="w-14 h-14 rounded-2xl border-2 border-dashed border-stone-300 flex items-center justify-center">
                                    <Plus className="w-6 h-6" />
                                  </div>
                                  <span className="text-xs uppercase tracking-[0.15em] font-medium">Add next piece</span>
                                </motion.button>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      </AnimatePresence>
                    </div>

                    {/* Swipe hint on mobile */}
                    <p className="md:hidden text-center text-[10px] text-stone-400 mt-2 italic">← Swipe to browse →</p>

                    {/* Page navigation */}
                    {lookbookItems.length > 0 && (
                      <div className="flex items-center justify-between mt-3">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                          disabled={currentPage === 0}
                          className="flex items-center gap-1.5 px-3 py-2 text-stone-500 hover:text-stone-800 disabled:opacity-30 disabled:cursor-not-allowed rounded-xl hover:bg-white transition-all text-sm"
                        >
                          <ChevronLeft className="w-4 h-4" />
                          <span className="hidden md:inline">Previous</span>
                        </motion.button>

                        <div className="flex items-center gap-1.5">
                          {/* Mobile: per-item dots */}
                          <div className="md:hidden flex items-center gap-1">
                            {lookbookItems.map((_, i) => (
                              <button
                                key={i}
                                onClick={() => setCurrentPage(i)}
                                className={`w-2 h-2 rounded-full transition-all ${
                                  currentPage === i ? 'bg-stone-800 w-5' : 'bg-stone-300 hover:bg-stone-400'
                                }`}
                              />
                            ))}
                          </div>
                          {/* Desktop: per-spread dots */}
                          <div className="hidden md:flex items-center gap-1">
                            {Array.from({ length: Math.ceil(lookbookItems.length / 2) }, (_, i) => (
                              <button
                                key={i}
                                onClick={() => setCurrentPage(i * 2)}
                                className={`w-2 h-2 rounded-full transition-all ${
                                  Math.floor(currentPage / 2) === i ? 'bg-stone-800 w-5' : 'bg-stone-300 hover:bg-stone-400'
                                }`}
                              />
                            ))}
                          </div>
                        </div>

                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setCurrentPage(Math.min(lookbookItems.length - 1, currentPage + 1))}
                          disabled={currentPage >= lookbookItems.length - 1}
                          className="flex items-center gap-1.5 px-3 py-2 text-stone-500 hover:text-stone-800 disabled:opacity-30 disabled:cursor-not-allowed rounded-xl hover:bg-white transition-all text-sm"
                        >
                          <span className="hidden md:inline">Next</span>
                          <ChevronRight className="w-4 h-4" />
                        </motion.button>
                      </div>
                    )}
                  </div>
                )}

                {/* Creative Vision CTA */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  onClick={() => navigate('/ai-studio')}
                  className="mt-6 p-5 rounded-2xl border border-stone-200 bg-white/80 cursor-pointer hover:shadow-md transition-all group"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full border border-stone-300 flex items-center justify-center flex-shrink-0">
                      <Camera className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-stone-800 group-hover:text-amber-700 transition-colors">✨ Bring your looks to life</h4>
                      <p className="text-xs text-stone-500 mt-0.5">Upload your saved photos and describe the scene — place yourself on a beach, runway, city street or anywhere you imagine.</p>
                    </div>
                    <ExternalLink className="w-5 h-5 text-stone-300 group-hover:text-amber-500 transition-colors flex-shrink-0" />
                  </div>
                </motion.div>
                    </motion.div>
                  )}
                  </AnimatePresence>
                  </React.Fragment>
                ))}
              </div>
            )}
        </div>
      </div>

      {/* Create Collection Modal */}
      <AnimatePresence>
      {showCreateModal && (
        <motion.div 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        >
          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl"
          >
            <h3 className="text-xl font-light text-stone-800 mb-5" style={{ fontFamily: 'Georgia, serif' }}>New Collection</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-600 mb-1">Name</label>
                <input
                  type="text"
                  value={newLookbook.name}
                  onChange={(e) => setNewLookbook({ ...newLookbook, name: e.target.value })}
                  placeholder="e.g., Summer Vacation Outfits"
                  className="w-full px-4 py-2.5 border border-stone-200 rounded-xl focus:ring-2 focus:ring-suede-400 focus:border-transparent bg-stone-50/50 transition-colors"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-stone-600 mb-1">Description</label>
                <textarea
                  value={newLookbook.description}
                  onChange={(e) => setNewLookbook({ ...newLookbook, description: e.target.value })}
                  placeholder="What's this collection about?"
                  rows={2}
                  className="w-full px-4 py-2.5 border border-stone-200 rounded-xl focus:ring-2 focus:ring-suede-400 focus:border-transparent bg-stone-50/50 transition-colors"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-stone-600 mb-1">Style</label>
                <div className="grid grid-cols-3 gap-2">
                  {STYLES.map(style => (
                    <button
                      key={style.id}
                      onClick={() => setNewLookbook({ ...newLookbook, style: style.id, customStyle: '' })}
                      className={`px-3 py-2 text-sm rounded-xl border transition-all ${
                        newLookbook.style === style.id && !newLookbook.customStyle
                          ? 'border-suede-400 bg-suede-50 text-suede-600 shadow-sm'
                          : 'border-stone-200 hover:border-stone-300 text-stone-600'
                      }`}
                    >
                      {getStyleEmoji(style.id)} {style.name}
                    </button>
                  ))}
                  <button
                    onClick={() => setNewLookbook({ ...newLookbook, style: 'custom', customStyle: newLookbook.customStyle || '' })}
                    className={`px-3 py-2 text-sm rounded-xl border transition-all ${
                      newLookbook.style === 'custom' || newLookbook.customStyle
                        ? 'border-suede-400 bg-suede-50 text-suede-600 shadow-sm'
                        : 'border-stone-200 hover:border-stone-300 text-stone-600'
                    }`}
                  >
                    ✏️ Custom
                  </button>
                </div>
                {(newLookbook.style === 'custom' || newLookbook.customStyle) && (
                  <input
                    type="text"
                    value={newLookbook.customStyle || ''}
                    onChange={(e) => setNewLookbook({ ...newLookbook, style: 'custom', customStyle: e.target.value })}
                    placeholder="Type your custom style..."
                    className="mt-2 w-full px-3 py-2 border border-stone-200 rounded-xl text-sm focus:ring-2 focus:ring-suede-400 focus:border-transparent"
                  />
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-stone-600 mb-1">Season</label>
                <div className="flex flex-wrap gap-2">
                  {SEASONS.map(season => (
                    <button
                      key={season.id}
                      onClick={() => setNewLookbook({ ...newLookbook, season: season.id, customSeason: '' })}
                      className={`px-3 py-1.5 text-sm rounded-full border transition-all ${
                        newLookbook.season === season.id && !newLookbook.customSeason
                          ? 'border-suede-400 bg-suede-50 text-suede-600 shadow-sm'
                          : 'border-stone-200 hover:border-stone-300 text-stone-600'
                      }`}
                    >
                      {getSeasonEmoji(season.id)} {season.name}
                    </button>
                  ))}
                  <button
                    onClick={() => setNewLookbook({ ...newLookbook, season: 'custom', customSeason: newLookbook.customSeason || '' })}
                    className={`px-3 py-1.5 text-sm rounded-full border transition-all ${
                      newLookbook.season === 'custom' || newLookbook.customSeason
                        ? 'border-suede-400 bg-suede-50 text-suede-600 shadow-sm'
                        : 'border-stone-200 hover:border-stone-300 text-stone-600'
                    }`}
                  >
                    ✏️ Custom
                  </button>
                </div>
                {(newLookbook.season === 'custom' || newLookbook.customSeason) && (
                  <input
                    type="text"
                    value={newLookbook.customSeason || ''}
                    onChange={(e) => setNewLookbook({ ...newLookbook, season: 'custom', customSeason: e.target.value })}
                    placeholder="Type your custom season..."
                    className="mt-2 w-full px-3 py-2 border border-stone-200 rounded-xl text-sm focus:ring-2 focus:ring-suede-400 focus:border-transparent"
                  />
                )}
              </div>
              
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2.5 text-stone-500 hover:text-stone-700 rounded-xl hover:bg-stone-100 transition-colors"
              >
                Cancel
              </button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleCreateLookbook}
                disabled={!newLookbook.name.trim()}
                className="px-5 py-2.5 bg-stone-800 hover:bg-stone-900 text-white rounded-full transition-all disabled:opacity-50 font-medium text-xs uppercase tracking-[0.15em]"
              >
                Create
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
      </AnimatePresence>

      {/* Add Item Modal */}
      <AnimatePresence>
      {showAddItemModal && selectedLookbook && (
        <motion.div 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        >
          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white rounded-3xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl"
          >
            <div className="p-5 border-b border-stone-100 flex items-center justify-between">
              <h3 className="text-lg font-light text-stone-800" style={{ fontFamily: 'Georgia, serif' }}>Add to {selectedLookbook.name}</h3>
              <button onClick={() => setShowAddItemModal(false)} className="p-2 hover:bg-stone-100 rounded-xl transition-colors">
                <X className="w-5 h-5 text-stone-400" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-stone-100 px-5 overflow-x-auto">
              {[
                { key: 'wardrobe', label: 'Wardrobe', count: wardrobeItems.length },
                { key: 'tryon', label: 'Try-Ons', count: tryonResults.length },
                { key: 'saved', label: 'Saved', count: savedProducts.length },
                { key: 'studio', label: 'Studio', count: studioSaves.length }
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setAddItemTab(tab.key)}
                  className={`px-4 py-3 text-xs uppercase tracking-[0.15em] font-medium border-b-2 transition-colors ${
                    addItemTab === tab.key
                      ? 'border-stone-800 text-stone-800'
                      : 'border-transparent text-stone-400 hover:text-stone-600'
                  }`}
                >
                  {tab.label} {tab.count > 0 && <span className="ml-1 text-xs text-stone-400">({tab.count})</span>}
                </button>
              ))}
            </div>
            
            <div className="overflow-y-auto max-h-[55vh] p-5">
              {/* Wardrobe Items */}
              {addItemTab === 'wardrobe' && (
                wardrobeItems.length > 0 ? (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {wardrobeItems.map(item => (
                      <motion.button
                        key={item.id}
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.96 }}
                        onClick={() => handleAddItem('wardrobe', item.id)}
                        className="aspect-square rounded-2xl overflow-hidden border-2 border-transparent hover:border-suede-400 transition-all shadow-sm hover:shadow-md"
                      >
                        <img
                          src={item.image_url || '/placeholder-fashion.jpg'}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      </motion.button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Image className="w-10 h-10 mx-auto text-stone-300 mb-2" />
                    <p className="text-stone-400 text-sm">No wardrobe items yet</p>
                  </div>
                )
              )}
              
              {/* Try-On Results */}
              {addItemTab === 'tryon' && (
                tryonResults.length > 0 ? (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {tryonResults.map(item => (
                      <motion.button
                        key={item.id}
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.96 }}
                        onClick={() => handleAddItem('tryon', item.id)}
                        className="aspect-square rounded-2xl overflow-hidden border-2 border-transparent hover:border-suede-400 transition-all shadow-sm hover:shadow-md"
                      >
                        <img
                          src={item.result_image_url || '/placeholder-fashion.jpg'}
                          alt="Try-on result"
                          className="w-full h-full object-cover"
                        />
                      </motion.button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Image className="w-10 h-10 mx-auto text-stone-300 mb-2" />
                    <p className="text-stone-400 text-sm">No try-on results yet</p>
                  </div>
                )
              )}
              
              {/* Saved Products */}
              {addItemTab === 'saved' && (
                savedProducts.length > 0 ? (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {savedProducts.map(item => (
                      <motion.button
                        key={item.id}
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.96 }}
                        onClick={() => handleAddItem('saved', item.id)}
                        className="aspect-square rounded-2xl overflow-hidden border-2 border-transparent hover:border-suede-400 transition-all shadow-sm hover:shadow-md"
                      >
                        <img
                          src={item.image_url || '/placeholder-fashion.jpg'}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      </motion.button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Image className="w-10 h-10 mx-auto text-stone-300 mb-2" />
                    <p className="text-stone-400 text-sm">No saved products yet</p>
                  </div>
                )
              )}
              
              {/* Studio Saves (Style Studio + AI Studio) */}
              {addItemTab === 'studio' && (
                studioSaves.length > 0 ? (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {studioSaves.map((item, idx) => {
                      const imgSrc = item.thumbnail || item.image_url || item.image || '/placeholder-fashion.jpg';
                      return (
                        <motion.button
                          key={item.id || idx}
                          whileHover={{ scale: 1.04 }}
                          whileTap={{ scale: 0.96 }}
                          onClick={() => handleAddItem('studio', null, item)}
                          className="relative aspect-square rounded-2xl overflow-hidden border-2 border-transparent hover:border-suede-400 transition-all shadow-sm hover:shadow-md"
                        >
                          <img
                            src={imgSrc}
                            alt={item.title || item.name || 'Studio save'}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute top-1.5 left-1.5">
                            <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-md backdrop-blur-sm bg-black/40 text-white">
                              {item.source === 'ai-studio' ? 'AI' : 'Style'}
                            </span>
                          </div>
                        </motion.button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Image className="w-10 h-10 mx-auto text-stone-300 mb-2" />
                    <p className="text-stone-400 text-sm">No studio saves yet</p>
                    <p className="text-stone-400 text-xs mt-1">Create outfits in Style Studio or AI Studio and save them</p>
                  </div>
                )
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
      </AnimatePresence>

      {/* Edit Collection Modal */}
      <AnimatePresence>
      {editingLookbook && (
        <motion.div 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        >
          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl"
          >
            <h3 className="text-xl font-light text-stone-800 mb-5" style={{ fontFamily: 'Georgia, serif' }}>Edit Collection</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-600 mb-1">Name</label>
                <input
                  type="text"
                  value={editingLookbook.name}
                  onChange={(e) => setEditingLookbook({ ...editingLookbook, name: e.target.value })}
                  className="w-full px-4 py-2.5 border border-stone-200 rounded-xl focus:ring-2 focus:ring-suede-400 focus:border-transparent bg-stone-50/50 transition-colors"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-stone-600 mb-1">Description</label>
                <textarea
                  value={editingLookbook.description || ''}
                  onChange={(e) => setEditingLookbook({ ...editingLookbook, description: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2.5 border border-stone-200 rounded-xl focus:ring-2 focus:ring-suede-400 focus:border-transparent bg-stone-50/50 transition-colors"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-stone-600 mb-1">Style</label>
                <div className="grid grid-cols-3 gap-2">
                  {STYLES.map(style => (
                    <button
                      key={style.id}
                      onClick={() => setEditingLookbook({ ...editingLookbook, style: style.id })}
                      className={`px-3 py-2 text-sm rounded-xl border transition-all ${
                        editingLookbook.style === style.id
                          ? 'border-suede-400 bg-suede-50 text-suede-600 shadow-sm'
                          : 'border-stone-200 hover:border-stone-300 text-stone-600'
                      }`}
                    >
                      {getStyleEmoji(style.id)} {style.name}
                    </button>
                  ))}
                </div>
              </div>
              
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setEditingLookbook(null)}
                className="px-4 py-2.5 text-stone-500 hover:text-stone-700 rounded-xl hover:bg-stone-100 transition-colors"
              >
                Cancel
              </button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleUpdateLookbook}
                className="px-5 py-2.5 bg-stone-800 hover:bg-stone-900 text-white rounded-full transition-all font-medium text-xs uppercase tracking-[0.15em]"
              >
                Save Changes
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
      </AnimatePresence>

    </div>
  );
}
